import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig";

import { useNavigate, useSearchParams } from 'react-router-dom';

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "name-asc", label: "Name: A to Z" },
];

export default function SidebarFilter({ onChange, mobileOpen, onClose, sortBy, onSortChange }) {
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const [categories, setCategories] = useState([]);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentCategory = searchParams.get("category") || "";

  // Handle click outside for sort dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch categories from products
  useEffect(() => {
    async function fetchCategories() {
      try {
        const snap = await getDocs(collection(db, "products"));
        const cats = new Set();
        snap.docs.forEach((doc) => {
          const cat = doc.data().category;
          if (cat && cat.trim()) {
            cats.add(cat.trim());
          }
        });
        setCategories(Array.from(cats).sort());
      } catch (e) {
        console.error("Error fetching categories:", e);
      }
    }
    fetchCategories();
  }, []);

  const apply = () => {
    onChange &&
      onChange({
        min: min ? Number(min) : undefined,
        max: max ? Number(max) : undefined,
      });
    if (onClose) onClose();
  };

  const reset = () => {
    setMin("");
    setMax("");
    onChange && onChange({});
    if (onClose) onClose();
  };

  const handleCategoryClick = (cat) => {
    if (cat === currentCategory) {
      // Already selected, go to all collections
      navigate("/collections");
    } else {
      navigate(`/collections?category=${encodeURIComponent(cat)}`);
    }
    if (onClose) onClose();
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`mobile-filter-overlay ${mobileOpen ? 'open' : ''}`}
        onClick={onClose}
      />

      <div className={`sidebar-filter white-card p-4 ${mobileOpen ? 'open' : ''}`}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="text-dark fw-bold fs-5">Filters</div>
          {/* Close button for mobile */}
          <div className="mobile-only sidebar-close-btn text-dark" onClick={onClose} style={{ cursor: 'pointer' }}>
            ✕
          </div>
        </div>

        {/* Sort Section */}
        <div className="filter-section">
          <div className="fw-bold mb-3" style={{ color: '#64748b', fontSize: '0.95rem' }}>Sort By</div>
          <div className="creative-sort-container" ref={sortDropdownRef}>
            <div 
              className={`creative-sort-trigger ${isSortOpen ? 'open' : ''}`}
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <div className="d-flex align-items-center gap-2">
                <span>{sortOptions.find(o => o.value === sortBy)?.label || 'Newest First'}</span>
              </div>
              <svg className={`sort-chevron ${isSortOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <div className={`creative-sort-menu ${isSortOpen ? 'open' : ''}`}>
              {sortOptions.map((option) => (
                <div 
                  key={option.value}
                  className={`creative-sort-option ${sortBy === option.value ? 'selected' : ''}`}
                  onClick={() => {
                    onSortChange && onSortChange(option.value);
                    setIsSortOpen(false);
                  }}
                >
                  <span className="sort-label">{option.label}</span>
                  {sortBy === option.value && (
                    <svg className="check-icon ms-auto" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <hr className="my-4" style={{ borderTop: '1px solid #e2e8f0', opacity: 1 }} />

        {/* Categories Section */}
        <div className="filter-section">
          <div className="fw-bold mb-3" style={{ color: '#64748b', fontSize: '0.95rem' }}>Categories</div>
          <div className="category-filter-list d-flex flex-wrap gap-2">
            <div
              className={`category-filter-item ${!currentCategory ? 'active' : ''}`}
              onClick={() => { navigate("/collections"); if (onClose) onClose(); }}
            >
              All Collections
            </div>
            {categories.map((cat) => (
              <div
                key={cat}
                className={`category-filter-item ${currentCategory === cat ? 'active' : ''}`}
                onClick={() => handleCategoryClick(cat)}
              >
                {cat}
              </div>
            ))}
          </div>
        </div>

        <hr className="my-4" style={{ borderTop: '1px solid #e2e8f0', opacity: 1 }} />

        {/* Price Range Section */}
        <div className="filter-section">
          <div className="fw-bold mb-3" style={{ color: '#64748b', fontSize: '0.95rem' }}>Price Range</div>
          <div className="d-flex align-items-center gap-3">
            <input
              type="number"
              placeholder="Min"
              value={min}
              onChange={(e) => setMin(e.target.value)}
              className="form-control shadow-none"
              style={{ borderRadius: '10px', border: '1px solid #e2e8f0', color: '#334155', padding: '10px 14px' }}
            />
            <span style={{ color: '#94a3b8' }}>-</span>
            <input
              type="number"
              placeholder="Max"
              value={max}
              onChange={(e) => setMax(e.target.value)}
              className="form-control shadow-none"
              style={{ borderRadius: '10px', border: '1px solid #e2e8f0', color: '#334155', padding: '10px 14px' }}
            />
          </div>
        </div>

        <hr className="my-4" style={{ borderTop: '1px solid #e2e8f0', opacity: 1 }} />

        {/* Actions */}
        <div className="filter-actions d-flex gap-3">
          <button
            className="btn w-50 fw-bold border-0"
            style={{ backgroundColor: '#f8fafc', color: '#0f172a', borderRadius: '50px', padding: '10px 0' }}
            onClick={reset}
          >
            Reset
          </button>
          <button
            className="btn btn-primary w-50 fw-bold hover-lift border-0"
            style={{ borderRadius: '50px', padding: '10px 0' }}
            onClick={apply}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}

